import { type InsightsMessage } from '../../hooks/useInsightsStream';

interface ToolGroup {
  key: string;
  node?: string;
  step?: number;
  localStep?: number;
  firstIndex: number;
  chunks: InsightsMessage[];
}

export interface ToolFeedback {
  key: string;
  label: string;
  summary: string;
  details: string;
  step?: number;
  globalStep?: number;
  status: 'pending' | 'running' | 'completed';
  isGenerateSql: boolean;
  sqlSnippet: string | null;
}

interface ToolFeedbackState {
  toolFeedback: ToolFeedback[];
  currentActivity: ToolFeedback | null;
  latestSqlSnippet: string | null;
}

const TOOL_USE_BLOCK_TYPE = 'tool_use';
const TOOL_TYPE = 'tool';
const MAX_TOOL_DETAILS_LENGTH = 250_000;

const extractTextFromBlock = (block: unknown): string => {
  if (!block || typeof block !== 'object') {
    return '';
  }

  const candidate = block as { type?: string; text?: unknown };
  if (candidate.type === 'text' && typeof candidate.text === 'string') {
    return candidate.text;
  }

  return '';
};

export const formatToolStepLabel = (label: string): string => {
  const match = label.match(/\bstep\s+(\d+)\b/i);
  if (!match) {
    return label;
  }

  const stepNumber = match[1];
  const name = label
    .replace(/\s*[–-]\s*/g, ' - ')
    .replace(/\s*-\s*step\s+\d+\b/i, '')
    .trim();

  if (!name) {
    return `Step ${stepNumber}`;
  }

  return `Step ${stepNumber}: ${name}`;
};

export const parseSqlCandidate = (value: unknown): string | null => {
  if (value == null) {
    return null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    try {
      const parsed = JSON.parse(trimmed);
      return parseSqlCandidate(parsed);
    } catch {
      if (/(\bselect\b|\bwith\b)/i.test(trimmed)) {
        return trimmed;
      }

      return null;
    }
  }

  if (Array.isArray(value)) {
    for (const entry of value) {
      const result = parseSqlCandidate(entry);
      if (result) {
        return result;
      }
    }

    return null;
  }

  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    if (typeof record.sql_query === 'string') {
      return record.sql_query.trim();
    }

    if (typeof record.sql === 'string') {
      return record.sql.trim();
    }

    for (const nestedValue of Object.values(record)) {
      const result = parseSqlCandidate(nestedValue);
      if (result) {
        return result;
      }
    }
  }

  return null;
};

export const formatResultTimestampLabel = (metadata: Record<string, unknown> | undefined): string | undefined => {
  if (!metadata) {
    return undefined;
  }

  const timestampCandidates: unknown[] = [
    metadata.timestamp,
    metadata.created_at,
    metadata.createdAt,
    metadata.event_timestamp,
  ];
  const candidate = timestampCandidates.find((value) => typeof value === 'string' && value.trim().length > 0);
  if (typeof candidate !== 'string') {
    return undefined;
  }

  const parsedDate = new Date(candidate);
  if (Number.isNaN(parsedDate.getTime())) {
    return undefined;
  }

  return parsedDate.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export const formatMessageContent = (content: unknown): string => {
  if (typeof content === 'string') {
    return content;
  }

  if (Array.isArray(content)) {
    return content
      .map((item) => {
        if (typeof item === 'string') {
          return item;
        }

        const text = extractTextFromBlock(item);
        return text || JSON.stringify(item);
      })
      .join(' ');
  }

  const blockText = extractTextFromBlock(content);
  if (blockText) {
    return blockText;
  }

  return JSON.stringify(content);
};

const extractSqlSnippetFromChunks = (chunks: InsightsMessage[]): string | null => {
  for (const chunk of chunks) {
    if (chunk.type !== TOOL_TYPE) {
      continue;
    }

    const sql = parseSqlCandidate(chunk.content);
    if (sql) {
      return sql;
    }
  }

  return null;
};

const isToolUseBlock = (value: unknown): value is { type: string; name?: string; input?: unknown } => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as { type?: string };
  return candidate.type === TOOL_USE_BLOCK_TYPE;
};

const deriveToolGroups = (messages: InsightsMessage[]): ToolGroup[] => {
  let lastHumanMessageIndex = -1;
  messages.forEach((message, index) => {
    if (message.type === 'human') {
      lastHumanMessageIndex = index;
    }
  });

  const groups = new Map<string, ToolGroup>();
  messages.forEach((message, index) => {
    if (index <= lastHumanMessageIndex) {
      return;
    }

    const isToolMessage = message.type === TOOL_TYPE;
    const hasToolUse = Array.isArray(message.content) && message.content.some((block) => isToolUseBlock(block));
    if (!isToolMessage && !hasToolUse) {
      return;
    }

    const metadata = message.metadata ?? {};
    const node = typeof metadata.langgraph_node === 'string' ? metadata.langgraph_node : undefined;
    const step = typeof metadata.langgraph_step === 'number' ? metadata.langgraph_step : undefined;
    const baseId = message.id || 'unknown';
    const key = `${node ?? 'unknown'}:${step ?? 0}:${baseId}`;

    const existing = groups.get(key) ?? { key, node, step, firstIndex: index, chunks: [] };
    existing.firstIndex = Math.min(existing.firstIndex, index);
    existing.chunks.push(message);
    groups.set(key, existing);
  });

  const ordered = Array.from(groups.values()).sort((a, b) => a.firstIndex - b.firstIndex);
  const stepOrder = new Map<number, number>();

  ordered.forEach((group) => {
    if (typeof group.step !== 'number') {
      return;
    }

    if (!stepOrder.has(group.step)) {
      stepOrder.set(group.step, stepOrder.size + 1);
    }
  });

  return ordered.map((group) => ({
    ...group,
    localStep: typeof group.step === 'number' ? stepOrder.get(group.step) : undefined,
  }));
};

const deriveToolFeedback = (groups: ToolGroup[], isThreadLoading: boolean): ToolFeedback[] => {
  return groups.map((group) => {
    const toolUses = group.chunks
      .flatMap((msg) => {
        if (!Array.isArray(msg.content)) {
          return [];
        }

        return msg.content.filter((block) => isToolUseBlock(block));
      })
      .map((block) => block as { name?: string; input?: unknown });

    const toolNames = toolUses
      .map((block) => (typeof block.name === 'string' ? block.name : undefined))
      .filter((name): name is string => Boolean(name));

    const hasResponse = group.chunks.some((msg) => msg.type === TOOL_TYPE);
    const labelParts: string[] = [];
    if (toolNames.length > 0) {
      labelParts.push(toolNames[0]);
    } else if (group.node) {
      labelParts.push(group.node);
    } else {
      labelParts.push('Tool call');
    }

    if (typeof group.localStep === 'number') {
      labelParts.push(`step ${group.localStep}`);
    }

    const summaryParts: string[] = [];
    if (toolNames.length > 0) {
      summaryParts.push(`Calling ${toolNames.join(', ')}`);
    } else if (group.node) {
      summaryParts.push(`Activity from ${group.node}`);
    }

    summaryParts.push(hasResponse ? 'Response captured' : 'Waiting for response');

    const toolInputParts: string[] = [];
    let lastStructuredToolInput: unknown = null;
    const toolResultChunks: string[] = [];

    group.chunks.forEach((msg) => {
      if (msg.type === TOOL_TYPE) {
        const text = formatMessageContent(msg.content);
        if (text) {
          toolResultChunks.push(text);
        }

        return;
      }

      if (!Array.isArray(msg.content)) {
        return;
      }

      msg.content.forEach((block) => {
        if (!isToolUseBlock(block)) {
          return;
        }

        if (typeof block.input === 'string') {
          toolInputParts.push(block.input);
        } else if (block.input != null) {
          lastStructuredToolInput = block.input;
        }
      });
    });

    let inputDetails = '';
    let inputCandidate: unknown = null;
    if (toolInputParts.length > 0) {
      inputCandidate = toolInputParts.join('');
    } else if (lastStructuredToolInput != null) {
      inputCandidate = lastStructuredToolInput;
    }

    if (inputCandidate != null) {
      const sqlFromInput = parseSqlCandidate(inputCandidate);
      if (sqlFromInput) {
        inputDetails = sqlFromInput;
      } else if (typeof inputCandidate === 'string') {
        inputDetails = inputCandidate;
      } else {
        try {
          inputDetails = JSON.stringify(inputCandidate);
        } catch {
          inputDetails = String(inputCandidate);
        }
      }
    }

    const resultDetails = toolResultChunks.join('\n\n');
    const globalStepDetails = typeof group.step === 'number' ? `Global step: ${group.step}` : '';
    const rawDetails = [globalStepDetails, inputDetails, resultDetails].filter(Boolean).join('\n\n');
    const details =
      rawDetails.length > MAX_TOOL_DETAILS_LENGTH
        ? `${rawDetails.slice(0, MAX_TOOL_DETAILS_LENGTH)}\n\n[truncated]`
        : rawDetails;

    const isGenerateSql = toolNames.includes('generate_sql');
    let status: ToolFeedback['status'];
    if (hasResponse || !isThreadLoading) {
      status = 'completed';
    } else if (isThreadLoading && (toolNames.length > 0 || group.node)) {
      status = 'running';
    } else {
      status = 'pending';
    }

    return {
      key: group.key,
      label: labelParts.join(' – '),
      summary: summaryParts.join(' • '),
      details: details || 'No additional details available yet.',
      step: typeof group.localStep === 'number' ? group.localStep : undefined,
      globalStep: typeof group.step === 'number' ? group.step : undefined,
      status,
      isGenerateSql,
      sqlSnippet: isGenerateSql ? extractSqlSnippetFromChunks(group.chunks) : null,
    };
  });
};

export const deriveToolFeedbackState = (messages: InsightsMessage[], isThreadLoading: boolean): ToolFeedbackState => {
  const toolGroups = deriveToolGroups(messages);
  const toolFeedback = deriveToolFeedback(toolGroups, isThreadLoading);

  let currentActivity: ToolFeedback | null = null;
  if (toolFeedback.length === 0) {
    if (isThreadLoading) {
      currentActivity = {
        key: 'current-status',
        label: 'Thinking',
        summary: 'Preparing response...',
        details: '',
        status: 'running',
        isGenerateSql: false,
        sqlSnippet: null,
      };
    }
  } else {
    const reversedFeedback = [...toolFeedback].reverse();
    currentActivity =
      reversedFeedback.find((feedback) => feedback.status === 'running') ??
      reversedFeedback.find((feedback) => feedback.status === 'pending') ??
      reversedFeedback[0];
  }

  const latestSqlSnippet =
    [...toolFeedback].reverse().find((feedback) => feedback.isGenerateSql && feedback.sqlSnippet)?.sqlSnippet ?? null;

  return {
    toolFeedback,
    currentActivity,
    latestSqlSnippet,
  };
};

export const formatThreadDate = (isoDate: string): string => {
  try {
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) {
      return 'Unknown';
    }

    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'Unknown';
  }
};
